import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { Plus, Edit, Trash2, Upload, X, ImageIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { useAdmin } from '@/context/AdminContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminProducts = () => {
  const { token } = useAdmin();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [imagePreviews, setImagePreviews] = useState([]);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    image: '',
    images: [],
    category: 'graphic',
    sizes: ['P', 'M', 'G', 'GG'],
    featured: false
  });

  // Get auth token from context or localStorage
  const getAuthToken = () => {
    return token || localStorage.getItem('admin_token');
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      toast.error('Erro ao carregar produtos');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (files) => {
    if (!files || files.length === 0) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    const maxFiles = 5;
    
    // Check total images
    const currentCount = formData.images.length;
    const newFiles = Array.from(files).slice(0, maxFiles - currentCount);
    
    if (newFiles.length === 0) {
      toast.error(`Máximo de ${maxFiles} imagens por produto.`);
      return;
    }

    setUploading(true);

    for (const file of newFiles) {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`${file.name}: Tipo não suportado. Use JPG, PNG, WEBP ou GIF.`);
        continue;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name}: Arquivo muito grande. Máximo 5MB.`);
        continue;
      }

      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);

        const authToken = getAuthToken();
        
        if (!authToken) {
          toast.error('Sessão expirada. Faça login novamente.');
          return;
        }

        const response = await axios.post(`${API}/admin/upload`, formDataUpload, {
          headers: {
            'Authorization': `Bearer ${authToken}`,
          },
        });

        if (response.data.success) {
          // Use relative URL so it works on any domain
          const imageUrl = response.data.url;
          
          setFormData(prev => {
            const newImages = [...prev.images, imageUrl];
            return {
              ...prev,
              images: newImages,
              image: prev.image || imageUrl // Set first image as main
            };
          });
          
          setImagePreviews(prev => [...prev, imageUrl]);
        }
      } catch (error) {
        console.error('Upload error:', error);
        if (error.response?.status === 401) {
          toast.error('Sessão expirada. Faça login novamente.');
        } else {
          toast.error(error.response?.data?.detail || `Erro ao enviar ${file.name}`);
        }
      }
    }

    setUploading(false);
    toast.success('Imagens enviadas com sucesso!');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleImageUpload(files);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.images.length === 0) {
      toast.error('Por favor, adicione pelo menos uma imagem do produto');
      return;
    }

    try {
      const productData = {
        ...formData,
        price: parseFloat(formData.price),
        image: formData.images[0] // Main image is the first one
      };

      const authToken = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${authToken}` } };

      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.id}`, productData, config);
        toast.success('Produto atualizado com sucesso!');
      } else {
        await axios.post(`${API}/admin/products`, productData, config);
        toast.success('Produto criado com sucesso!');
      }
      
      setShowModal(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      toast.error('Erro ao salvar produto');
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Tem certeza que deseja deletar este produto?')) return;
    
    try {
      const authToken = getAuthToken();
      await axios.delete(`${API}/admin/products/${productId}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      toast.success('Produto deletado com sucesso!');
      fetchProducts();
    } catch (error) {
      toast.error('Erro ao deletar produto');
    }
  };

  const openEditModal = (product) => {
    setEditingProduct(product);
    const images = product.images || [product.image];
    setFormData({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      image: product.image,
      images: images,
      category: product.category,
      sizes: product.sizes,
      featured: product.featured
    });
    setImagePreviews(images);
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingProduct(null);
    setImagePreviews([]);
    setFormData({
      name: '',
      description: '',
      price: '',
      image: '',
      images: [],
      category: 'graphic',
      sizes: ['P', 'M', 'G', 'GG'],
      featured: false
    });
  };

  const handleSizeToggle = (size) => {
    if (formData.sizes.includes(size)) {
      setFormData({ ...formData, sizes: formData.sizes.filter(s => s !== size) });
    } else {
      setFormData({ ...formData, sizes: [...formData.sizes, size] });
    }
  };

  const removeImage = (index) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    const newPreviews = imagePreviews.filter((_, i) => i !== index);
    
    setFormData({
      ...formData,
      images: newImages,
      image: newImages[0] || ''
    });
    setImagePreviews(newPreviews);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const setMainImage = (index) => {
    const newImages = [...formData.images];
    const [selected] = newImages.splice(index, 1);
    newImages.unshift(selected);
    
    const newPreviews = [...imagePreviews];
    const [selectedPreview] = newPreviews.splice(index, 1);
    newPreviews.unshift(selectedPreview);
    
    setFormData({
      ...formData,
      images: newImages,
      image: selected
    });
    setImagePreviews(newPreviews);
    toast.success('Imagem principal definida!');
  };

  return (
    <AdminLayout>
      <div data-testid="admin-products-page">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 lg:mb-8">
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-white">Produtos</h1>
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="bg-primary text-black font-bold uppercase tracking-wider px-4 sm:px-6 py-2 sm:py-3 hover:bg-white transition-colors flex items-center gap-2 text-sm sm:text-base w-full sm:w-auto justify-center"
            data-testid="add-product-button"
          >
            <Plus className="h-4 w-4 sm:h-5 sm:w-5" />
            Adicionar Produto
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="h-16 w-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {products.map((product) => (
              <div key={product.id} className="bg-zinc-900/50 border border-white/10 overflow-hidden" data-testid={`product-${product.id}`}>
                <div className="aspect-[3/4] relative bg-zinc-800">
                  <img 
                    src={product.image} 
                    alt={product.name} 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling?.classList.remove('hidden');
                    }}
                  />
                  <div className="hidden absolute inset-0 flex items-center justify-center bg-zinc-800">
                    <ImageIcon className="h-16 w-16 text-zinc-600" />
                  </div>
                  {product.featured && (
                    <span className="absolute top-2 left-2 bg-primary text-black px-2 py-1 text-xs font-bold">DESTAQUE</span>
                  )}
                  {product.images && product.images.length > 1 && (
                    <span className="absolute top-2 right-2 bg-black/70 text-white px-2 py-1 text-xs font-bold">
                      {product.images.length} fotos
                    </span>
                  )}
                </div>
                <div className="p-3 sm:p-4">
                  <h3 className="font-heading text-base lg:text-lg font-bold text-white mb-1 sm:mb-2 truncate">{product.name}</h3>
                  <p className="text-xs sm:text-sm text-zinc-400 mb-2 sm:mb-3 line-clamp-2">{product.description}</p>
                  <p className="font-mono text-lg lg:text-xl text-primary font-bold mb-3 sm:mb-4">R$ {product.price.toFixed(2)}</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => openEditModal(product)}
                      className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-white py-2 flex items-center justify-center gap-1 sm:gap-2 transition-colors text-xs sm:text-sm"
                      data-testid="edit-product-button"
                    >
                      <Edit className="h-3 w-3 sm:h-4 sm:w-4" />
                      <span className="hidden sm:inline">Editar</span>
                    </button>
                    <button
                      onClick={() => handleDelete(product.id)}
                      className="flex-1 bg-red-500/20 hover:bg-red-500/30 text-red-500 py-2 flex items-center justify-center gap-1 sm:gap-2 transition-colors text-xs sm:text-sm"
                      data-testid="delete-product-button"
                    >
                      <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                      <span className="hidden sm:inline">Deletar</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal */}
        <AnimatePresence>
          {showModal && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setShowModal(false)}
                className="fixed inset-0 bg-black/80 z-50"
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto"
              >
                <div className="bg-zinc-900 border border-white/10 w-full max-w-2xl my-4 sm:my-0" onClick={(e) => e.stopPropagation()}>
                  <div className="p-4 sm:p-6">
                    <h2 className="font-heading text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6">
                      {editingProduct ? 'Editar Produto' : 'Novo Produto'}
                    </h2>

                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <label className="block text-sm uppercase tracking-wider text-zinc-400 mb-2">Nome *</label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="w-full bg-zinc-800 border border-zinc-700 focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 text-white"
                          required
                          data-testid="product-name-input"
                        />
                      </div>

                      <div>
                        <label className="block text-sm uppercase tracking-wider text-zinc-400 mb-2">Descrição *</label>
                        <textarea
                          value={formData.description}
                          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                          rows={3}
                          className="w-full bg-zinc-800 border border-zinc-700 focus:border-primary focus:ring-1 focus:ring-primary p-4 text-white resize-none"
                          required
                          data-testid="product-description-input"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm uppercase tracking-wider text-zinc-400 mb-2">Preço (R$) *</label>
                          <input
                            type="number"
                            step="0.01"
                            value={formData.price}
                            onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                            className="w-full bg-zinc-800 border border-zinc-700 focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 text-white"
                            required
                            data-testid="product-price-input"
                          />
                        </div>

                        <div>
                          <label className="block text-sm uppercase tracking-wider text-zinc-400 mb-2">Categoria *</label>
                          <select
                            value={formData.category}
                            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                            className="w-full bg-zinc-800 border border-zinc-700 focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 text-white"
                            required
                            data-testid="product-category-select"
                          >
                            <option value="graphic">Gráficas</option>
                            <option value="limited">Edição Limitada</option>
                          </select>
                        </div>
                      </div>

                      {/* Multiple Images Upload Area */}
                      <div>
                        <label className="block text-sm uppercase tracking-wider text-zinc-400 mb-2">
                          Imagens do Produto * (máx. 5)
                        </label>
                        
                        {/* Image Previews Grid */}
                        {imagePreviews.length > 0 && (
                          <div className="grid grid-cols-3 gap-3 mb-4">
                            {imagePreviews.map((preview, index) => (
                              <div key={index} className="relative group">
                                <img 
                                  src={preview} 
                                  alt={`Preview ${index + 1}`} 
                                  className={`w-full h-32 object-cover border-2 ${
                                    index === 0 ? 'border-primary' : 'border-white/10'
                                  }`}
                                />
                                {index === 0 && (
                                  <span className="absolute top-1 left-1 bg-primary text-black px-2 py-0.5 text-xs font-bold">
                                    PRINCIPAL
                                  </span>
                                )}
                                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                  {index !== 0 && (
                                    <button
                                      type="button"
                                      onClick={() => setMainImage(index)}
                                      className="bg-primary text-black p-2 text-xs font-bold hover:bg-white transition-colors"
                                      title="Definir como principal"
                                    >
                                      Principal
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => removeImage(index)}
                                    className="bg-red-500 text-white p-2 hover:bg-red-600 transition-colors"
                                    title="Remover"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Upload Area - Show if less than 5 images */}
                        {formData.images.length < 5 && (
                          <div
                            onDrop={handleDrop}
                            onDragOver={handleDragOver}
                            onClick={() => fileInputRef.current?.click()}
                            className={`border-2 border-dashed border-zinc-700 hover:border-primary p-6 text-center cursor-pointer transition-colors ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
                            data-testid="image-upload-area"
                          >
                            {uploading ? (
                              <div className="flex flex-col items-center">
                                <div className="h-10 w-10 border-4 border-primary border-t-transparent rounded-full animate-spin mb-3" />
                                <p className="text-zinc-400 text-sm">Enviando imagens...</p>
                              </div>
                            ) : (
                              <>
                                <Upload className="h-10 w-10 text-zinc-500 mx-auto mb-3" />
                                <p className="text-zinc-400 text-sm mb-1">Clique ou arraste imagens aqui</p>
                                <p className="text-xs text-zinc-600">JPG, PNG, WEBP ou GIF (máx. 5MB cada)</p>
                                <p className="text-xs text-primary mt-2">{formData.images.length}/5 imagens</p>
                              </>
                            )}
                          </div>
                        )}
                        
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/jpeg,image/png,image/webp,image/gif"
                          onChange={(e) => handleImageUpload(e.target.files)}
                          className="hidden"
                          multiple
                          data-testid="file-input"
                        />
                      </div>

                      <div>
                        <label className="block text-sm uppercase tracking-wider text-zinc-400 mb-2">Tamanhos *</label>
                        <div className="flex gap-2">
                          {['P', 'M', 'G', 'GG', 'XG'].map(size => (
                            <button
                              key={size}
                              type="button"
                              onClick={() => handleSizeToggle(size)}
                              className={`px-4 py-2 border transition-colors ${
                                formData.sizes.includes(size)
                                  ? 'border-primary bg-primary text-black font-bold'
                                  : 'border-zinc-700 text-zinc-400 hover:border-white'
                              }`}
                              data-testid={`size-${size}-button`}
                            >
                              {size}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          id="featured"
                          checked={formData.featured}
                          onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                          className="w-5 h-5 bg-zinc-800 border-zinc-700"
                          data-testid="featured-checkbox"
                        />
                        <label htmlFor="featured" className="text-sm uppercase tracking-wider text-zinc-400">
                          Produto em destaque (aparece na homepage)
                        </label>
                      </div>

                      <div className="flex gap-3 pt-4">
                        <button
                          type="button"
                          onClick={() => setShowModal(false)}
                          className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-white py-3 uppercase tracking-wider transition-colors"
                          data-testid="cancel-button"
                        >
                          Cancelar
                        </button>
                        <button
                          type="submit"
                          disabled={uploading}
                          className="flex-1 bg-primary hover:bg-white text-black font-bold py-3 uppercase tracking-wider transition-colors disabled:opacity-50"
                          data-testid="submit-product-button"
                        >
                          {editingProduct ? 'Atualizar' : 'Criar'} Produto
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </AdminLayout>
  );
};

export default AdminProducts;
